import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Button } from "@components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Switch } from "@components/ui/switch";
import { Label } from "@components/ui/label";
import { toggleBundleStatus } from "./createPluginApi";
import { toast } from "@components/ui/use-toast";
import { useMutation, useQueryClient } from "react-query";
import PageLoader from "@components/loaders/PageLoader";

type Props = {
  title: string;
  setTitle: (title: string) => void;
  description: string;
  setDescription: (description: string) => void;
  selectedLogicName: string | null;
  handleAddLogic: () => void;
  closeAddLogicForm: () => void;
  open?: boolean;
  onClose?: () => void;
  croneBundleTime: string;
  setCroneBundleTime: (time: string) => void;
  bundleDependency: string;
  setBundleDependency: (dependency: string) => void;
  logicType: any;
  isEdit?: number | null;
  plugin_id?: string | null;
  bundle_id?: number | string | null;
  initialBundleActivate?: boolean;
};

const CreateBundleDialog: React.FC<Props> = ({
  title,
  setTitle,
  description,
  setDescription,
  selectedLogicName,
  handleAddLogic,
  closeAddLogicForm,
  croneBundleTime,
  setCroneBundleTime,
  bundleDependency,
  setBundleDependency,
  logicType,
  isEdit,
  plugin_id,
  bundle_id,
  initialBundleActivate,
}) => {
  const [bundleStatus, setBundleStatus] = React.useState<"active" | "deactive">(
    initialBundleActivate === false ? "deactive" : "active",
  );
  const [loading, setLoading] = React.useState(false);
  const queryClient = useQueryClient();

  // Update status when initialBundleActivate changes
  React.useEffect(() => {
    if (initialBundleActivate !== undefined) {
      setBundleStatus(initialBundleActivate ? "active" : "deactive");
    }
  }, [initialBundleActivate]);

  // Mutation for toggling bundle status
  const toggleStatusMutation = useMutation({
    mutationFn: ({ status }: { status: "active" | "deactive" }) =>
      toggleBundleStatus(plugin_id!, String(bundle_id!), status),

    onMutate: () => {
      setLoading(true);
    },

    onSuccess: (_, variables) => {
      setBundleStatus(variables.status);

      // Invalidate and refetch the logic page data to get updated status
      queryClient.invalidateQueries(["fetchLogicPageData", plugin_id]);

      const currentStatus =
        variables.status == "active" ? "activated" : "deactivated";

      toast({
        variant: "success",
        title: "Bundle Status Updated",
        description: `Bundle has been ${currentStatus} successfully.`,
        duration: 3000,
      });
    },

    onError: (res: any) => {
      toast({
        variant: "destructive",
        title: "Status Update Failed",
        description: res?.message || "Failed to update bundle status.",
        duration: 3000,
      });
    },

    onSettled: () => {
      setLoading(false);
    },
  });

  // Handle switch toggle
  const handleStatusToggle = (checked: boolean) => {
    if (!plugin_id || !bundle_id) {
      toast({
        variant: "destructive",
        title: "Cannot Update Status",
        description: "Bundle must be saved before changing status.",
        duration: 3000,
      });
      return;
    }

    const newStatus = checked ? "active" : "deactive";
    if (isEdit != null) {
      toggleStatusMutation.mutate({ status: newStatus });
    } else {
      toast({
        variant: "destructive",
        title: "Cannot Update Status",
        description: "Bundle must be saved before changing status.",
        duration: 3000,
      });
    }
  };

  const DEPENDENT_BUNDLES = logicType.flatMap((item: any) => item.data || []);
  const requiresCronTime =
    selectedLogicName === "Data List" || selectedLogicName === "Save Logs";
  const parsedCronTime = Number(croneBundleTime);
  const isCronTimeInvalid =
    requiresCronTime &&
    (!!croneBundleTime || croneBundleTime === "0") &&
    (!Number.isFinite(parsedCronTime) || parsedCronTime < 15);

  const handleSubmitBundle = () => {
    if (isCronTimeInvalid) {
      toast({
        variant: "destructive",
        title: "Invalid Cron Time",
        description: "Cron time must be at least 15 minutes.",
        duration: 3000,
      });
      return;
    }

    handleAddLogic();
  };

  return (
    <Dialog open={true} onOpenChange={closeAddLogicForm}>
      <DialogContent
        className={`sm:max-w-[30rem] ${bundleStatus === "deactive" ? "" : ""}`}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <PageLoader show={loading} />
        <DialogHeader>
          <div className="flex items-center justify-between w-full border-b pb-2 mt-3">
            <DialogTitle className="text-md flex items-center  gap-2">
              {isEdit == null ? "Create" : "Edit"} {selectedLogicName} Bundle{" "}
            </DialogTitle>

            <div className="flex items-center gap-2 border p-2 rounded-xl">
              <Label htmlFor="bundle-status" className="capitalize">
                Bundle {bundleStatus == "active" ? "Active" : "Inactive"}
              </Label>
              <Switch
                id="bundle-status"
                checked={bundleStatus === "active"}
                onCheckedChange={handleStatusToggle}
                disabled={
                  !plugin_id || !bundle_id || toggleStatusMutation.isLoading
                }
                className="data-[state=checked]:bg-primaryGreen peer-focus:ring-primaryGreen h-7 w-11 rounded-full border border-secondary bg-primaryBackground transition-colors duration-200 ease-in-out"
              />
            </div>
          </div>
        </DialogHeader>

        {bundleStatus === "deactive" && (
          <div className="flex items-center gap-2 rounded-lg bg-lightRed p-4 text-sm text-primary border text-center">
            <p>This bundle is currently deactivated.</p>
          </div>
        )}

        <div className="flex flex-col gap-2 border p-4 rounded-md bg-transparent border-secondary">
          <div>
            <label className="block text-sm font-medium mb-1">
              Bundle Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 rounded-md border border-secondary bg-primary-background text-sm"
              placeholder={`Enter ${selectedLogicName} Bundle Title`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Bundle Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-2 rounded-md border border-secondary bg-primary-background text-sm"
              placeholder="Enter bundle description"
            />
          </div>
          {(selectedLogicName === "Data List" ||
            selectedLogicName === "Save Logs") && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Cron Time
              </label>
              <input
                type="text"
                value={croneBundleTime}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setCroneBundleTime(value);
                }}
                onBlur={() => {
                  if (isCronTimeInvalid) {
                    toast({
                      variant: "destructive",
                      title: "Invalid Cron Time",
                      description: "Cron time must be at least 15 minutes.",
                      duration: 3000,
                    });
                  }
                }}
                inputMode="numeric"
                className="w-full p-2 rounded-md border border-secondary bg-primary-background text-sm"
                placeholder="Enter cron time in minutes (e.g., 60 for 1 hour)"
              />
            </div>
          )}

          <div>
            <p className="block text-sm font-medium mb-1">
              Dependent bundle{" "}
              <span className="text-muted-foreground">(optional)</span>
            </p>
            <Select
              value={bundleDependency}
              onValueChange={(v) => {
                setBundleDependency(v);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select dependent Bundle" />
              </SelectTrigger>

              <SelectContent className="rounded-xl">
                <SelectItem value="none">Select dependent Bundle</SelectItem>
                {DEPENDENT_BUNDLES.map((m: any) => (
                  <SelectItem key={m.id} value={String(m.id)}>
                    {m.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex w-full justify-end gap-2">
            <Button
              variant="outline"
              className="mt-2"
              onClick={closeAddLogicForm}
            >
              Cancel
            </Button>

            <Button
              className="mt-2"
              onClick={handleSubmitBundle}
              disabled={
                !title.trim() ||
                !description.trim() ||
                selectedLogicName === "DataList" ||
                (selectedLogicName === "Save Logs" && !croneBundleTime) ||
                bundleStatus === "deactive"
              }
            >
              {isEdit == null ? "Create" : "Update"} Bundle
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateBundleDialog;
